// gameData.js
// Bloom & Blight — the Leaflings card game.
// Static definitions: species, cards, biomes, weather, deck construction.

/*******************************************************************************
 * SPECIES
 * Each species grows when light >= lightNeed AND waterMin <= water <= waterMax.
 * `difficulty` is used by the AI to decide planting order (hardest first) and
 * shown to players as a hint (easy plants are best saved as closers).
 ******************************************************************************/
export const SPECIES = {
  calathea: {
    id: "calathea",
    name: "Calathea",
    lightNeed: 6,
    waterMin: 4,
    waterMax: 6,
    difficulty: 5,
    blurb: "A thirsty diva. Loves a long drink and a bright room.",
  },
  cactus: {
    id: "cactus",
    name: "Cactus",
    lightNeed: 6,
    waterMin: 1,
    waterMax: 1,
    difficulty: 4,
    blurb: "Wants blazing sun and exactly one sip. No more, no less.",
  },
  pilea: {
    id: "pilea",
    name: "Pilea",
    lightNeed: 4,
    waterMin: 3,
    waterMax: 4,
    difficulty: 3,
    blurb: "The friendship plant. Balanced, like all things should be.",
  },
  zz: {
    id: "zz",
    name: "ZZ Plant",
    lightNeed: 4,
    waterMin: 2,
    waterMax: 3,
    difficulty: 2,
    blurb: "An easy win in the greenhouse. A perfect closer.",
  },
  snake: {
    id: "snake",
    name: "Snake Plant",
    lightNeed: 5,
    waterMin: 1,
    waterMax: 2,
    difficulty: 1,
    blurb: "Nearly unkillable. Give it sun, keep it dry, and it does the rest.",
  },
};

export const SPECIES_IDS = Object.keys(SPECIES);

/*******************************************************************************
 * CARDS
 * type: "plant" | "treatment" | "blight"
 * Plant cards use the species id as their card id.
 ******************************************************************************/
export const CARDS = {
  // ---- Plants (see SPECIES) ----
  ...Object.fromEntries(
    SPECIES_IDS.map((id) => [
      id,
      {
        id,
        type: "plant",
        name: SPECIES[id].name,
        icon: "🌱",
        text:
          "Plant in an empty pot — or compost onto your " +
          SPECIES[id].name +
          " already in play for +1 ☀️ and +1 💧.",
      },
    ])
  ),

  // ---- Treatments ----
  fertilizer: {
    id: "fertilizer",
    type: "treatment",
    name: "Fertilizer",
    icon: "🧪",
    text: "Give one of your plants +1 ☀️ and up to +1 💧 (never past its sweet spot).",
  },
  growLamp: {
    id: "growLamp",
    type: "treatment",
    name: "Grow Lamp",
    icon: "🔦",
    status: true,
    text: "Status: this plant gains +1 ☀️ with every forecast, rain or shine.",
  },
  irrigation: {
    id: "irrigation",
    type: "treatment",
    name: "Irrigation",
    icon: "🚿",
    text: "Pour in or drain out up to 3 💧 on one of your plants.",
  },
  repot: {
    id: "repot",
    type: "treatment",
    name: "Repot",
    icon: "🪴",
    text: "A plant with 3+ ☀️ only: clear its blight, then +1 ☀️, +1 💧, or fresh dry soil (−3 💧).",
  },
  humidifier: {
    id: "humidifier",
    type: "treatment",
    name: "Humidifier",
    icon: "🫧",
    status: true,
    text: "Status: weather cannot change this plant's 💧. Watering and cards still can.",
  },
  shopping: {
    id: "shopping",
    type: "treatment",
    name: "Shopping Trip",
    icon: "🛍️",
    text: "Draw 2 cards.",
  },
  recycle: {
    id: "recycle",
    type: "treatment",
    name: "Recycle",
    icon: "♻️",
    text: "Take any treatment or plant card from your discard pile back into your hand.",
  },
  planter: {
    id: "planter",
    type: "treatment",
    name: "Planter",
    icon: "🏺",
    text: "Plant any plant card straight from your deck. Uses your plant action.",
  },
  greenhouse: {
    id: "greenhouse",
    type: "treatment",
    name: "Greenhouse",
    icon: "🏡",
    text: "Adjust up to 3 of your plants: +1 ☀️, −1 💧, or +1 💧 — one adjustment per plant.",
  },

  // ---- Blights ----
  umbrella: {
    id: "umbrella",
    type: "blight",
    name: "Umbrella",
    icon: "☂️",
    status: true,
    text: "Blight: shades an enemy plant from every forecast — no ☀️, no 💧, no drying out.",
  },
  rootRot: {
    id: "rootRot",
    type: "blight",
    name: "Root Rot",
    icon: "🦠",
    text: "Flood an enemy plant with +5 💧.",
  },
  shovel: {
    id: "shovel",
    type: "blight",
    name: "Shovel",
    icon: "🪓",
    text: "Dig up an enemy plant with less than 4 ☀️ and less than 4 💧. It goes to their discard pile.",
  },
  pests: {
    id: "pests",
    type: "blight",
    name: "Pests",
    icon: "🐛",
    text: "Reset an enemy plant's ☀️ and 💧 to zero.",
  },
};

/*******************************************************************************
 * DECK CONSTRUCTION
 * Every deck = 12 plant cards (2 species x6, 3 species x4, or 4 species x3)
 * plus the standard 22-card kit of treatments and blights below.
 ******************************************************************************/
export const COPIES_FOR_SPECIES_COUNT = { 2: 6, 3: 4, 4: 3 };

export const STANDARD_KIT = [
  // Ten treatments + eight blights = a configurable 30-card deck.
  ...Array(2).fill("fertilizer"),
  "irrigation",
  "growLamp",
  "shopping",
  "greenhouse",
  "repot",
  "humidifier",
  "recycle",
  "planter",
  ...Array(2).fill("umbrella"),
  ...Array(2).fill("rootRot"),
  ...Array(2).fill("shovel"),
  "pests",
];

/*******************************************************************************
 * WEATHER + BIOMES
 * Between every turn the forecast die is rolled; odds depend on the biome
 * (rolled once at the start of the match).
 ******************************************************************************/
export const WEATHER = {
  sunny: {
    id: "sunny",
    name: "Sunny",
    icon: "☀️",
    light: 2,
    water: -2,
    text: "All plants get +2 ☀️ and dry out −2 💧.",
  },
  cloudy: {
    id: "cloudy",
    name: "Cloudy",
    icon: "⛅",
    light: 1,
    water: -1,
    text: "All plants get +1 ☀️ and dry out −1 💧.",
  },
  rain: {
    id: "rain",
    name: "Rain",
    icon: "🌧️",
    light: 0,
    water: 2,
    text: "All plants get +2 💧.",
  },
};

// odds = sixths of the forecast die: [sunny, cloudy, rain]
export const BIOMES = {
  carmel: {
    id: "carmel",
    name: "Carmel-by-the-Sea",
    icon: "🌊",
    odds: { sunny: 2, cloudy: 3, rain: 1 },
    blurb: "Marine-layer mornings, golden afternoons.",
  },
  sonoran: {
    id: "sonoran",
    name: "Sonoran Desert",
    icon: "🌵",
    odds: { sunny: 4, cloudy: 1, rain: 1 },
    blurb: "Relentless sun. Bring a canteen.",
  },
  mosswood: {
    id: "mosswood",
    name: "Mossy Northwest",
    icon: "🌲",
    odds: { sunny: 1, cloudy: 3, rain: 2 },
    blurb: "Skip the watering can, pack a raincoat.",
  },
  glasshouse: {
    id: "glasshouse",
    name: "The Old Glasshouse",
    icon: "🏡",
    odds: { sunny: 2, cloudy: 2, rain: 2 },
    blurb: "A little of everything, every single day.",
  },
};

export const BIOME_IDS = Object.keys(BIOMES);

// Gauge caps — light and water can never go below 0 or above these.
export const MAX_LIGHT = 10;
export const MAX_WATER = 10;

export const GARDEN_SLOTS = 3;
export const PETALS_TO_WIN = 5;
export const STARTING_HAND = 5;
